import { Link, useNavigate } from 'react-router-dom';
import { Bars3Icon, XMarkIcon, UserIcon, DocumentTextIcon, ArrowRightStartOnRectangleIcon } from '@heroicons/react/24/outline';
import { ReactNode, useCallback, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import AccountSettings from '../../pages/AccountSettings/AccountSettings';
import useClickOutside from '../../hooks/useClickOutside';
import UserService from '../../services/UserService';

export interface DropdownOption {
  label: string;
  href: string;
}

export interface Breadcrumb {
  label: string;
  href?: string;
  icon?: ReactNode;
  dropdownTitle?: string;
  dropdownOptions?: DropdownOption[];
}

interface NavbarProps {
  breadcrumbs: Breadcrumb[];
  sidebarItems?: ReactNode;
}

const BreadcrumbSeparator = () => (
  <div className="w-0 h-3 origin-top-left rotate-[15deg] outline outline-1 outline-offset-[-0.50px] outline-white-alpha-20" />
);

const BreadcrumbDropdown: React.FC<{
  crumb: Breadcrumb;
  isLast: boolean;
}> = ({ crumb, isLast }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useClickOutside(ref, useCallback(() => setOpen(false), []), open);

  const hasDropdown = crumb.dropdownOptions && crumb.dropdownOptions.length > 0;

  const handleToggle = hasDropdown ? () => setOpen((prev) => !prev) : undefined;

  return (
    <div ref={ref} className="relative flex justify-center items-center gap-1 min-w-0">
      {crumb.icon && (
        <div className="w-3.5 h-3.5 flex items-center justify-center text-white-alpha-80">
          {crumb.icon}
        </div>
      )}
      {hasDropdown ? (
        <button
          type="button"
          className="flex items-center gap-1 text-center text-white typo-label min-w-0"
          onClick={handleToggle}
        >
          <span className="truncate">{crumb.label}</span>
          <img src="/selector.svg" alt="" className="w-4 h-4 flex-shrink-0" />
        </button>
      ) : crumb.href && !isLast ? (
        <Link
          to={crumb.href}
          className="text-center text-white typo-label truncate"
        >
          {crumb.label}
        </Link>
      ) : (
        <span className="text-center text-white typo-label truncate">
          {crumb.label}
        </span>
      )}
      {open && hasDropdown && (
        <div className="absolute top-full left-0 mt-2 min-w-[180px] py-1 bg-white rounded-md shadow-lg outline outline-1 outline-neutral-10 z-50 whitespace-normal">
          {crumb.dropdownTitle && (
            <div className="px-3 py-1.5 text-neutral-60 typo-label border-b border-neutral-5">
              {crumb.dropdownTitle}
            </div>
          )}
          {crumb.dropdownOptions!.map((option) => (
            <button
              key={option.href}
              type="button"
              className={`w-full px-3 py-2 text-left typo-body hover:bg-neutral-5 transition-colors ${
                option.label === crumb.label ? 'text-primary-500' : 'text-neutral'
              }`}
              onClick={() => {
                navigate(option.href);
                setOpen(false);
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const AccountDropdown: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { data: userData } = useQuery({
    queryKey: ['user'],
    queryFn: () => UserService.getUser(),
  });
  const user = userData?.data;

  useClickOutside(ref, useCallback(() => setOpen(false), []), open);

  return (
    <>
      <div ref={ref} className="relative">
        <button
          type="button"
          className="h-8 px-3 rounded-lg outline outline-1 outline-offset-[-1px] outline-white-alpha-10 flex justify-center items-center gap-1"
          onClick={() => setOpen((prev) => !prev)}
        >
          <UserIcon className="w-3.5 h-3.5 text-white-alpha-90" />
          <span className="text-center text-white-alpha-90 typo-label">
            {user ? `${user.firstName} ${user.lastName}` : 'Mon compte'}
          </span>
        </button>
        {open && (
          <div className="absolute top-full right-0 mt-2 min-w-[200px] py-1 bg-white rounded-md shadow-lg outline outline-1 outline-neutral-10 z-50">
            <button
              type="button"
              className="w-full px-3 py-2 text-left typo-body hover:bg-neutral-5 transition-colors text-neutral flex items-center gap-2"
              onClick={() => {
                setAccountOpen(true);
                setOpen(false);
              }}
            >
              <UserIcon className="w-4 h-4 text-neutral-60" />
              Mon compte
            </button>
            {process.env.REACT_APP_DOCS_URL && (
              <a
                href={process.env.REACT_APP_DOCS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full px-3 py-2 text-left typo-body hover:bg-neutral-5 transition-colors text-neutral flex items-center gap-2"
                onClick={() => setOpen(false)}
              >
                <DocumentTextIcon className="w-4 h-4 text-neutral-60" />
                Documentation
              </a>
            )}
            <div className="my-1 border-t border-neutral-10" />
            <button
              type="button"
              className="w-full px-3 py-2 text-left typo-body hover:bg-neutral-5 transition-colors text-error-500 flex items-center gap-2"
              onClick={() => {
                navigate('/logout');
                setOpen(false);
              }}
            >
              <ArrowRightStartOnRectangleIcon className="w-4 h-4" />
              Se déconnecter
            </button>
          </div>
        )}
      </div>
      <AccountSettings open={accountOpen} onClose={() => setAccountOpen(false)} />
    </>
  );
};

const Navbar: React.FC<NavbarProps> = ({ breadcrumbs, sidebarItems }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <>
      <div className="z-50 self-stretch h-14 bg-primary-500 border-b border-primary-400 backdrop-blur-[5.5px] flex flex-col justify-start items-center">
        <div className="self-stretch flex-1 inline-flex justify-start items-start">
          <div className="flex-1 self-stretch px-4 flex justify-start items-center gap-3 min-w-0">
            {/* Logo */}
            <Link to="/sites" className="w-6 h-6 flex-shrink-0">
              <img src="/logo_white.svg" alt="Follow" className="w-auto h-full" />
            </Link>

            {/* Breadcrumbs */}
            <div className="flex-1 min-w-0 flex items-center gap-3">
              {breadcrumbs.map((crumb, index) => (
                <div key={index} className="flex items-center gap-3 whitespace-nowrap min-w-0">
                  <BreadcrumbSeparator />
                  <BreadcrumbDropdown
                    crumb={crumb}
                    isLast={index === breadcrumbs.length - 1}
                  />
                </div>
              ))}
            </div>

            {/* Mobile hamburger button */}
            <button
              type="button"
              className="md:hidden flex-shrink-0 p-1 text-white"
              onClick={() => setMobileMenuOpen((prev) => !prev)}
            >
              {mobileMenuOpen ? (
                <XMarkIcon className="w-6 h-6" />
              ) : (
                <Bars3Icon className="w-6 h-6" />
              )}
            </button>
          </div>
          <div className="self-stretch px-4 hidden md:flex justify-start items-center gap-2">
            <AccountDropdown />
          </div>
        </div>
      </div>

      {/* Mobile sidebar overlay: full-width dropdown sized to its content, dim backdrop below */}
      {mobileMenuOpen && sidebarItems && (
        <div className="md:hidden fixed inset-0 top-14 z-50 flex flex-col">
          <div
            className="self-stretch bg-primary-500 border-b border-primary-400 shadow-lg"
            onClick={() => setMobileMenuOpen(false)}
          >
            {sidebarItems}
          </div>
          <div
            className="flex-1 bg-neutral-30"
            onClick={() => setMobileMenuOpen(false)}
          />
        </div>
      )}
    </>
  );
};

export default Navbar;
